'use client';

import { c as _c } from "react/compiler-runtime";
import { type GetOutcomeSuggestionsRow, useActiveWallet, useGetMarketChainMetadata, useGetOutcomeSuggestions, usePromoteOutcomeSuggestion, useTurnkeySigningDeps, useTurnkeySubmissionDeps } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import type { JSX } from 'react';
import { useRef, useState } from 'react';
import { TOAST_IDS } from '@/config/toast-ids';
import { useAddOutcomeMutation } from '@/hooks/proposals/use-add-outcome';
import { useLiveListData } from '@/hooks/sdk/use-live-list-data';
import { useIntersectionObserver } from '@/hooks/ui/use-intersection-observer';
import { type DynamicTranslator, resolveApiErrorMessage } from '@/lib/errors/resolve-api-error';
import { logger } from '@/lib/telemetry/logger';
import { getConnection } from '@/lib/tx-engine/connection';
import { useSubmitTransaction } from '@/lib/tx-engine/use-submit-transaction';
import { DURATION_EXIT_MS } from '@/lib/ui/easing';
import { dismissToast, showToast } from '@/lib/ui/toast';
import { CreatorReviewModal } from './creator/CreatorReviewModal';
import { LimitReachedModal } from './LimitReachedModal';
import { SUGGESTIONS_PER_USER_PER_MARKET, SuggestedOutcomesPanel } from './SuggestedOutcomesPanel';
import { effectiveLabel, effectiveTicker, type SuggestionWire } from './types';
import { SuggestOutcomeWizard } from './wizard/SuggestOutcomeWizard';
const VIEWPORT_PREFETCH_MARGIN = '400px';
interface SuggestedOutcomesSectionProps {
  marketPubkey: string;
  marketCreator: string;
  marketStatus: number;
  marketTitle?: string;
  onRequireSignIn?: () => void;
  externalOpen?: boolean;
  onExternalOpenChange?: (open: boolean) => void;
}
export function SuggestedOutcomesSection(t0) {
  const $ = _c(83);
  const {
    marketPubkey,
    marketCreator,
    marketStatus,
    marketTitle: t1,
    onRequireSignIn,
    externalOpen,
    onExternalOpenChange
  } = t0;
  const marketTitle = t1 === undefined ? "" : t1;
  const t = useTranslations("launchpad.suggestedOutcomes");
  const translateError = useTranslations() as unknown as DynamicTranslator;
  const {
    activeWallet
  } = useActiveWallet();
  const viewerPubkey = activeWallet?.address ?? null;
  const [internalWizardOpen, setInternalWizardOpen] = useState(false);
  const wizardOpen = externalOpen ?? internalWizardOpen;
  let t2;
  if ($[0] !== onExternalOpenChange) {
    t2 = next => {
      if (onExternalOpenChange !== undefined) {
        onExternalOpenChange(next);
      } else {
        setInternalWizardOpen(next);
      }
    };
    $[0] = onExternalOpenChange;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const setWizardOpen = t2;
  const [limitOpen, setLimitOpen] = useState(false);
  const [creatorReviewSuggestion, setCreatorReviewSuggestion] = useState(null);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {};
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const [reservedOutcomeIndexes, setReservedOutcomeIndexes] = useState(t3);
  const sectionRef = useRef(null);
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      rootMargin: VIEWPORT_PREFETCH_MARGIN,
      once: true
    };
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const {
    isIntersecting
  } = useIntersectionObserver(sectionRef, t4);
  const isEligible = marketStatus >= 0 && marketStatus <= 1;
  const suggestionsEnabled = isEligible && (isIntersecting || wizardOpen);
  const chainMetadataEnabled = isEligible && (wizardOpen || creatorReviewSuggestion !== null);
  const signingDeps = useTurnkeySigningDeps();
  const turnkeyDeps = useTurnkeySubmissionDeps();
  const submitTransaction = useSubmitTransaction();
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = ["marketPubkey", "marketConfig", "quoteMint", "eventSchedule", "outcomeCount", "creator", "status"];
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== marketPubkey) {
    t6 = {
      pubkey: marketPubkey,
      fields: t5
    };
    $[5] = marketPubkey;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== chainMetadataEnabled) {
    t7 = {
      enabled: chainMetadataEnabled
    };
    $[7] = chainMetadataEnabled;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const chainMetadataQuery = useGetMarketChainMetadata(t6, t7);
  let t8;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = getConnection();
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  const t9 = activeWallet ?? null;
  let t10;
  if ($[10] !== signingDeps || $[11] !== submitTransaction || $[12] !== t9 || $[13] !== turnkeyDeps) {
    t10 = {
      connection: t8,
      submitTransaction,
      activeWallet: t9,
      signingDeps,
      turnkeyDeps
    };
    $[10] = signingDeps;
    $[11] = submitTransaction;
    $[12] = t9;
    $[13] = turnkeyDeps;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  const addOutcomeMutation = useAddOutcomeMutation(t10);
  const promoteMutation = usePromoteOutcomeSuggestion();
  let t11;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = ["id", "marketPubkey", "proposalPubkey", "suggesterPubkey", "suggester", "label", "ticker", "imageUrl", "reason", "upvotes", "downvotes", "score", "status", "generationMode", "creatorEditedLabel", "creatorEditedTicker", "creatorEditedImageUrl", "promotedOutcomePubkey", "outcomeIndex", "promotedAt", "createdAt", "viewerKind"];
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] !== marketPubkey) {
    t12 = {
      pubkey: marketPubkey,
      viewportGated: true,
      fields: t11
    };
    $[16] = marketPubkey;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  let t13;
  if ($[18] !== suggestionsEnabled) {
    t13 = {
      enabled: suggestionsEnabled
    };
    $[18] = suggestionsEnabled;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  const query = useGetOutcomeSuggestions(t12, t13);
  const suggestions = useLiveListData(query.queryKey, query.rows, undefined, undefined, suggestionsEnabled);
  if (!isEligible) {
    return null;
  }
  let t14;
  if ($[20] !== suggestions || $[21] !== viewerPubkey) {
    t14 = viewerPubkey === null ? 0 : suggestions.filter(suggestion => suggestion.suggesterPubkey === viewerPubkey).length;
    $[20] = suggestions;
    $[21] = viewerPubkey;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  const usedByViewer = t14;
  const remainingSuggestions = Math.max(0, SUGGESTIONS_PER_USER_PER_MARKET - usedByViewer);
  let t15;
  if ($[23] !== suggestions) {
    t15 = suggestions.map(_temp);
    $[23] = suggestions;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  const existingSuggestionLabels = t15;
  const isCreator = viewerPubkey !== null && viewerPubkey === marketCreator;
  let t16;
  if ($[25] !== isCreator || $[26] !== onRequireSignIn || $[27] !== remainingSuggestions || $[28] !== setWizardOpen || $[29] !== viewerPubkey) {
    t16 = function handleSuggest() {
      if (viewerPubkey === null) {
        onRequireSignIn?.();
        return;
      }
      if (!isCreator && remainingSuggestions === 0) {
        setLimitOpen(true);
        return;
      }
      setWizardOpen(true);
    };
    $[25] = isCreator;
    $[26] = onRequireSignIn;
    $[27] = remainingSuggestions;
    $[28] = setWizardOpen;
    $[29] = viewerPubkey;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  const handleSuggest = t16;
  let t17;
  if ($[31] !== addOutcomeMutation || $[32] !== chainMetadataQuery.data || $[33] !== marketPubkey || $[34] !== t || $[35] !== translateError) {
    t17 = function fireAddOutcomeOnChain(suggestion_1, onSuccess) {
      const chainMeta = chainMetadataQuery.data;
      if (chainMeta === undefined) {
        showToast({
          id: TOAST_IDS.suggestOutcomeSubmitError,
          message: t("toastMarketInfoNotReady"),
          variant: "error"
        });
        return;
      }
      setReservedOutcomeIndexes(current => ({
        ...current,
        [suggestion_1.id]: chainMeta.outcomeCount
      }));
      showToast({
        id: TOAST_IDS.suggestOutcomeSubmitError,
        message: t("toastAddingOutcome"),
        variant: "loading",
        duration: Infinity
      });
      addOutcomeMutation.mutate({
        suggestionId: suggestion_1.id,
        marketPubkey: chainMeta.marketPubkey,
        marketConfigBase58: chainMeta.marketConfig,
        eventScheduleBase58: chainMeta.eventSchedule,
        outcomeIndex: chainMeta.outcomeCount,
        label: effectiveLabel(suggestion_1),
        ticker: effectiveTicker(suggestion_1)
      }, {
        onSuccess: () => {
          showToast({
            id: TOAST_IDS.suggestOutcomeSubmitError,
            message: t("toastOutcomeAdded"),
            variant: "success"
          });
          onSuccess?.();
        },
        onError: error => {
          dismissToast(TOAST_IDS.suggestOutcomeSubmitError);
          const message = resolveApiErrorMessage(error, translateError);
          if (message !== null) {
            showToast({
              id: TOAST_IDS.TX_FAILED,
              message,
              variant: "error"
            });
          }
          logger.warn("suggested_outcomes.add_outcome_failed", {
            marketPubkey,
            suggestionId: suggestion_1.id
          });
        }
      });
    };
    $[31] = addOutcomeMutation;
    $[32] = chainMetadataQuery.data;
    $[33] = marketPubkey;
    $[34] = t;
    $[35] = translateError;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  const fireAddOutcomeOnChain = t17;
  let t18;
  if ($[37] !== fireAddOutcomeOnChain || $[38] !== marketPubkey || $[39] !== promoteMutation) {
    t18 = async function handleConfirmPromote(suggestion_2) {
      try {
        await promoteMutation.mutateAsync({
          pubkey: marketPubkey,
          id: suggestion_2.id
        });
      } catch {
        return;
      }
      fireAddOutcomeOnChain(suggestion_2, () => setCreatorReviewSuggestion(null));
    };
    $[37] = fireAddOutcomeOnChain;
    $[38] = marketPubkey;
    $[39] = promoteMutation;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  const handleConfirmPromote = t18;
  let t19;
  if ($[41] !== handleSuggest || $[42] !== isIntersecting || $[43] !== marketCreator || $[44] !== query.fetchNextPage || $[45] !== query.hasNextPage || $[46] !== query.isFetchingNextPage || $[47] !== query.itemObserver || $[48] !== reservedOutcomeIndexes || $[49] !== suggestions || $[50] !== viewerPubkey) {
    t19 = isIntersecting ? <SuggestedOutcomesPanel marketCreator={marketCreator} viewerPubkey={viewerPubkey} rows={suggestions} reservedOutcomeIndexes={reservedOutcomeIndexes} itemObserver={query.itemObserver} hasNextPage={query.hasNextPage} isFetchingNextPage={query.isFetchingNextPage} fetchNextPage={query.fetchNextPage} onSuggest={handleSuggest} onAddToMarket={setCreatorReviewSuggestion} /> : null;
    $[41] = handleSuggest;
    $[42] = isIntersecting;
    $[43] = marketCreator;
    $[44] = query.fetchNextPage;
    $[45] = query.hasNextPage;
    $[46] = query.isFetchingNextPage;
    $[47] = query.itemObserver;
    $[48] = reservedOutcomeIndexes;
    $[49] = suggestions;
    $[50] = viewerPubkey;
    $[51] = t19;
  } else {
    t19 = $[51];
  }
  let t20;
  if ($[52] !== t19) {
    t20 = <div ref={sectionRef} className="min-h-px scroll-mt-4">{t19}</div>;
    $[52] = t19;
    $[53] = t20;
  } else {
    t20 = $[53];
  }
  let t21;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = [];
    $[54] = t21;
  } else {
    t21 = $[54];
  }
  const t22 = chainMetadataQuery.data?.outcomeCount ?? null;
  let t23;
  if ($[55] !== fireAddOutcomeOnChain || $[56] !== isCreator || $[57] !== setWizardOpen) {
    t23 = suggestion_3 => {
      setWizardOpen(false);
      if (isCreator) {
        fireAddOutcomeOnChain(suggestion_3);
      }
      window.setTimeout(() => {
        sectionRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start"
        });
      }, DURATION_EXIT_MS + 50);
    };
    $[55] = fireAddOutcomeOnChain;
    $[56] = isCreator;
    $[57] = setWizardOpen;
    $[58] = t23;
  } else {
    t23 = $[58];
  }
  let t24;
  if ($[59] !== existingSuggestionLabels || $[60] !== isCreator || $[61] !== marketPubkey || $[62] !== marketTitle || $[63] !== remainingSuggestions || $[64] !== setWizardOpen || $[65] !== t22 || $[66] !== t23 || $[67] !== wizardOpen) {
    t24 = <SuggestOutcomeWizard open={wizardOpen} onOpenChange={setWizardOpen} marketPubkey={marketPubkey} marketTitle={marketTitle} existingOutcomeLabels={t21} marketOutcomeCount={t22} existingSuggestionLabels={existingSuggestionLabels} remainingSuggestions={remainingSuggestions} creatorDirectAdd={isCreator} onSubmitted={t23} />;
    $[59] = existingSuggestionLabels;
    $[60] = isCreator;
    $[61] = marketPubkey;
    $[62] = marketTitle;
    $[63] = remainingSuggestions;
    $[64] = setWizardOpen;
    $[65] = t22;
    $[66] = t23;
    $[67] = wizardOpen;
    $[68] = t24;
  } else {
    t24 = $[68];
  }
  let t25;
  if ($[69] !== limitOpen) {
    t25 = <LimitReachedModal open={limitOpen} onOpenChange={setLimitOpen} />;
    $[69] = limitOpen;
    $[70] = t25;
  } else {
    t25 = $[70];
  }
  const t26 = creatorReviewSuggestion !== null;
  let t27;
  if ($[71] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = next_0 => {
      if (!next_0) {
        setCreatorReviewSuggestion(null);
      }
    };
    $[71] = t27;
  } else {
    t27 = $[71];
  }
  const t28 = promoteMutation.isPending || addOutcomeMutation.isPending;
  let t29;
  if ($[72] !== creatorReviewSuggestion || $[73] !== handleConfirmPromote || $[74] !== marketPubkey || $[75] !== t26 || $[76] !== t28) {
    t29 = <CreatorReviewModal open={t26} onOpenChange={t27} marketPubkey={marketPubkey} suggestion={creatorReviewSuggestion} onConfirm={handleConfirmPromote} isPending={t28} />;
    $[72] = creatorReviewSuggestion;
    $[73] = handleConfirmPromote;
    $[74] = marketPubkey;
    $[75] = t26;
    $[76] = t28;
    $[77] = t29;
  } else {
    t29 = $[77];
  }
  let t30;
  if ($[78] !== t20 || $[79] !== t24 || $[80] !== t25 || $[81] !== t29) {
    t30 = <>{t20}{t24}{t25}{t29}</>;
    $[78] = t20;
    $[79] = t24;
    $[80] = t25;
    $[81] = t29;
    $[82] = t30;
  } else {
    t30 = $[82];
  }
  return t30;
}
function _temp(suggestion_0) {
  return suggestion_0.label;
}